<template>
    <div class="notification-banner">
        <div class="notification-message-container cpointer" @click="onClickBanner">
            <template v-if="isEmailNotification && !isSystemNotif">
                <NotificationEmail type="banner" :notification="data.notif" />
            </template>
            <NotificationReminderActivity
                v-if="isReminderActivity"
                :notification="data.notif"
                :context="NOTIFICATION_CONTEXT.NOTIFICATION_BANNER"
            />
            <template v-else>
                <CompassLogo v-if="showLogo" type="icon" theme="light" :width="32" />
                <CompassAvatar v-else :initials="getInitialsAvatar" :src="getSourceAvatar" />
                <p v-if="isSystemNotif">
                    <span class="bold"> {{ data.linkedObject.name }} : </span>
                    <span v-if="isConsentNotification" class="bold">
                        {{ targetedStaffFullName }}
                    </span>
                    <span>{{ data.message }}</span>
                </p>
                <p v-else>
                    <span class="bold">
                        {{ $filters.capitalize(data.sender.firstname) }}
                        {{ $filters.capitalize(data.sender.lastname) }}
                    </span>
                    <span> {{ data.message }} {{ getTargetLabel(data) }}</span>
                    <span class="bold">{{ data.linkedObject.name }}</span>
                </p>
            </template>
        </div>
        <CompassIconButton
            class="notification-close-button"
            icon="close"
            size="small"
            @click="onCloseNotif"
        />
    </div>
</template>

<script>
import NotificationApi from 'api/notification/Notification';
import NotificationsMixins from 'mixins/NotificationsMixins.js';
import NotificationEmail from 'vcomponents/notifications/emails/NotificationEmail.vue';
import NotificationReminderActivity from 'vcomponents/notifications/activity/NotificationReminderActivity.vue';
import { NOTIFICATION_CONTEXT, EVENT_TYPES } from 'Dictionaries/notification/NotificationEnums';

export default {
    name: 'NotificationBanner',
    components: {
        NotificationEmail,
        NotificationReminderActivity,
    },
    mixins: [NotificationsMixins],
    props: {
        data: {
            type: Object,
            required: true,
        },
        isSystemNotif: {
            type: Boolean,
            required: false,
            default: false,
        },
        notifId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            NOTIFICATION_CONTEXT,
        };
    },
    computed: {
        isConsentNotification() {
            return (
                this.data.event === 'third.authorization.accepted' ||
                this.data.event === 'third.authorization.refused'
            );
        },
        isEmailNotification() {
            return this.data.event === 'email.received.notification';
        },
        isReminderActivity() {
            return this.data.event === EVENT_TYPES.AGENDA_ALERT_SENT;
        },
        targetedStaffFullName() {
            return `${this.data.targetedStaff.firstname} ${this.data.targetedStaff.lastname}`;
        },
        showLogo() {
            return this.isSystemNotif || this.data.sender.avatar.type === 'sellsyuserpic';
        },
        getInitialsAvatar() {
            return this.data.sender.avatar.type === 'initials' ? this.data.sender.avatar.value : '';
        },
        getSourceAvatar() {
            return this.data.sender.avatar.type === 'picture' ? this.data.sender.avatar.value : '';
        },
    },
    methods: {
        onClickBanner() {
            this.onCloseNotif();
            NotificationApi.markAsRead(this.notifId)
                .then(() => EventBus.emit('notification:unread:decrement'))
                .catch((err) => {
                    this.$pushToast({
                        type: 'error',
                        text: err.message,
                        close: true,
                        unique: true,
                        icon: true,
                    });
                });
            if (this.data.onClick) {
                this.data.onClick();
            } else {
                Utils.relocate(this.data.path, true);
            }
        },
        onCloseNotif() {
            this.$emit('clear', this.notifId);
        },
    },
};
</script>

<style scoped lang="scss">
@use '~@sellsy/styleguide/src/_variables' as *;

.notification-banner {
    align-items: flex-start;
    background-color: $white;
    border-radius: var(--spacing-0);
    box-shadow: 0 0 20px rgba(17, 17, 17, 0.2);
    display: flex;
    flex-direction: row;
    gap: var(--spacing-3);
    justify-content: space-between;
    margin-bottom: var(--spacing-3);
    padding: var(--spacing-1) var(--spacing-3);
    width: 420px;

    .notification-message-container {
        align-items: center;
        display: flex;
        flex-direction: row;
        gap: var(--spacing-1);
        padding: var(--spacing-0) 0;

        .compass-logo {
            align-items: center;
            background-color: $secondary-blue;
            border-radius: 50%;
            display: flex;
            flex-shrink: 0;
            height: 32px;
            justify-content: center;

            :deep(svg) {
                height: 20px;
                width: 20px;
            }
        }
    }

    p {
        font-size: 14px;
        line-height: 20px;
        margin: 0;
    }

    .notification-close-button {
        flex-shrink: 0;
    }
}
</style>
